/**
 * The signed-in staff member's profile, role and permission keys, in one call.
 *
 * Two providers need parts of the same answer at start-up: AuthProvider wants
 * the es_users row, PermissionsProvider wants the role and the effective
 * permission keys. Between them they used to issue five serial requests before
 * the page's own chunk was even fetched:
 *
 *   es_users (profile) -> es_users (role_id) -> rpc user_permission_keys
 *                      -> es_users (role_id) -> es_roles
 *
 * `bootstrap_staff_session()` answers all of it in one round trip, and the memo
 * below means the two providers asking separately still only produce one
 * request. Measured against production, the chain they replace ran 2.0 to 2.9
 * seconds on every route, dominated by round trips rather than query time.
 *
 * The RPC takes no arguments; identity comes from the JWT. The user id passed
 * here is used only as a cache key, never sent, so that a different signed-in
 * user can never be served the previous one's answer.
 */

import { supabase } from "./supabase";
import { memoizeBriefly } from "@/utils/memoizeBriefly";
import { registerIdentityCache } from "@/utils/identityCacheRegistry";
import type { UserProfile } from "@/lib/supabase/types";
import type { Role } from "@/types/database.types";

export interface StaffSessionBootstrap {
  profile: UserProfile | null;
  role: Role | null;
  permissionKeys: string[];
}

interface BootstrapEnvelope {
  profile?: UserProfile | null;
  role?: Role | null;
  permission_keys?: unknown;
}

/**
 * Latched once the database tells us the function is not there.
 *
 * A client can be deployed ahead of its migration, and a developer's branch
 * may point at a database without it. Rather than retry a call we know will
 * fail, we give up on it for the rest of the session and let every caller take
 * its own long way round — the same accommodation getUserPermissionKeys makes
 * for user_permission_keys.
 */
let rpcUnavailable = false;

/** True when the error means "no such function", not "the call failed". */
function isMissingFunction(error: { code?: string; message?: string }): boolean {
  if (error.code === "PGRST202" || error.code === "42883") return true;
  const message = String(error.message || "").toLowerCase();
  return (
    message.includes("could not find the function") ||
    message.includes("does not exist")
  );
}

async function fetchStaffSessionUncached(): Promise<StaffSessionBootstrap | null> {
  if (rpcUnavailable) return null;

  // GET, not the POST an RPC defaults to. PostgREST runs a GET in a read-only
  // transaction, and the view-as guard (lib/supabase/viewAsFetch.ts) lets reads
  // through, so this still works while a Super Admin is viewing as someone
  // else. As a POST the guard refuses it on every page load of that tab.
  const { data, error } = await supabase.rpc(
    "bootstrap_staff_session",
    {},
    { get: true },
  );

  if (error) {
    if (isMissingFunction(error)) {
      console.warn(
        "[sessionBootstrap] bootstrap_staff_session unavailable, falling back to per-table reads:",
        error.message,
      );
      rpcUnavailable = true;
      return null;
    }
    throw error;
  }

  const envelope = (data ?? {}) as BootstrapEnvelope;

  return {
    profile: envelope.profile ?? null,
    role: envelope.role ?? null,
    permissionKeys: Array.isArray(envelope.permission_keys)
      ? (envelope.permission_keys as unknown[]).filter(
          (key): key is string => typeof key === "string",
        )
      : [],
  };
}

/**
 * Resolve the session bundle, sharing one request across concurrent callers.
 *
 * Returns null when the RPC is unavailable, which callers must treat as "use
 * the old path", not as "this user has nothing".
 *
 * A result with no profile is not cached. That is the signup race — the auth
 * user exists and the trigger creating their es_users row has not committed —
 * and callers retry on a backoff to ride it out. Caching the empty answer
 * would hand every retry the same nothing.
 */
export const fetchStaffSession = memoizeBriefly(
  (_authUserId: string | null) => fetchStaffSessionUncached(),
  {
    ttlMs: 5000,
    key: (authUserId) => authUserId ?? "__no_session__",
    cacheable: (result) => result !== null && result.profile !== null,
  },
);

export function invalidateStaffSession(): void {
  fetchStaffSession.invalidate();
}

registerIdentityCache(invalidateStaffSession);

/** Test seam: forget that the function was ever reported missing. */
export function resetStaffSessionAvailability(): void {
  rpcUnavailable = false;
  fetchStaffSession.invalidate();
}
